import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Search,
  Sparkles,
  Share2,
  Heart,
  ArrowLeft,
  Shuffle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  INSIGHTS,
  INSIGHT_CATEGORIES,
  getRandomInsight,
  type Insight,
  type InsightCategory,
} from "@/data/elevateInsights";

const FAV_KEY = "elevate_moment_favorites";

const ElevateMoment = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<InsightCategory | "All">("All");
  const [favorites, setFavorites] = useState<string[]>([]);
  const [featured, setFeatured] = useState<Insight>(() => getRandomInsight());
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      if (raw) setFavorites(JSON.parse(raw));
    } catch {
      /* noop */
    }
  }, []);

  const persistFavorites = (next: string[]) => {
    setFavorites(next);
    try {
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
    } catch {
      /* noop */
    }
  };

  const toggleFavorite = (id: string) => {
    const next = favorites.includes(id)
      ? favorites.filter((f) => f !== id)
      : [...favorites, id];
    persistFavorites(next);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return INSIGHTS.filter((i) => {
      const matchCat = activeCategory === "All" || i.category === activeCategory;
      const matchQuery =
        !q ||
        i.text.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });
  }, [query, activeCategory]);

  const reveal = () => {
    setRevealing(true);
    setTimeout(() => {
      const pool =
        activeCategory === "All"
          ? INSIGHTS
          : INSIGHTS.filter((i) => i.category === activeCategory);
      setFeatured(getRandomInsight(pool, featured.id));
      setRevealing(false);
    }, 300);
  };

  const share = async (insight: Insight) => {
    const text = `"${insight.text}", Elevate Moment`;
    const url = `${window.location.origin}/elevate-moment`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Elevate Moment", text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Insight copied to clipboard");
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <>
      <Helmet>
        <title>Elevate Moment | Pause. Refocus. Gain Perspective.</title>
        <meta
          name="description"
          content="A curated collection of insights on business, focus, leadership, discipline, confidence, growth, and perspective from Elevate Global Solutions."
        />
        <meta property="og:title" content="Elevate Moment" />
        <meta
          property="og:description"
          content="Take a pause. Refocus. Gain perspective. A signature mindset experience from Elevate Global Solutions."
        />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/elevate-moment" />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--secondary)) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-secondary/10 blur-3xl"
        />
        <div className="container-narrow relative section-padding py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-primary-foreground/70 transition-colors hover:text-primary-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>

          <div className="mx-auto mt-8 max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground/80">
              <Sparkles className="h-3.5 w-3.5 text-secondary" /> Signature Experience
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight text-primary-foreground md:text-6xl">
              Elevate Moment
            </h1>
            <p className="mt-4 text-lg text-primary-foreground/75">
              Take a pause. Refocus. Gain perspective.
            </p>

            <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-sm md:p-10">
              <div
                className={`min-h-[140px] flex items-center justify-center transition-all duration-500 ${
                  revealing ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
                }`}
              >
                <div>
                  <p className="font-display text-2xl italic leading-relaxed text-primary-foreground/95 md:text-3xl">
                    “{featured.text}”
                  </p>
                  <p className="mt-4 text-xs uppercase tracking-[0.25em] text-secondary">
                    {featured.category}
                  </p>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                <Button size="lg" variant="secondary" onClick={reveal}>
                  <Shuffle className="mr-2 h-4 w-4" /> Reveal Insight
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => share(featured)}
                  className="text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                >
                  <Share2 className="mr-2 h-4 w-4" /> Share
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  onClick={() => toggleFavorite(featured.id)}
                  className="text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
                  aria-pressed={favorites.includes(featured.id)}
                  aria-label="Save to favorites"
                >
                  <Heart
                    className={`mr-2 h-4 w-4 ${
                      favorites.includes(featured.id) ? "fill-secondary text-secondary" : ""
                    }`}
                  />
                  {favorites.includes(featured.id) ? "Saved" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Collection */}
      <section className="bg-background">
        <div className="container-narrow section-padding py-16 md:py-20">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold md:text-3xl">
                The Collection
              </h2>
              <p className="mt-2 text-muted-foreground">
                Search, filter, and save the insights that resonate.
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search insights…"
                className="pl-9"
                aria-label="Search insights"
              />
            </div>
          </div>

          {/* Category filters */}
          <div className="mt-6 flex flex-wrap gap-2">
            {(["All", ...INSIGHT_CATEGORIES] as const).map((cat) => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-foreground hover:bg-muted"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Results */}
          {filtered.length === 0 ? (
            <div className="mt-12 rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground">No insights match your search.</p>
              <Button
                variant="link"
                onClick={() => {
                  setQuery("");
                  setActiveCategory("All");
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((insight) => {
                const fav = favorites.includes(insight.id);
                return (
                  <article
                    key={insight.id}
                    className="group relative flex flex-col rounded-xl border border-border bg-card p-6 transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
                      {insight.category}
                    </span>
                    <p className="mt-3 flex-1 font-display text-base italic leading-relaxed text-foreground">
                      “{insight.text}”
                    </p>
                    <div className="mt-5 flex items-center justify-end gap-1 border-t border-border pt-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => share(insight)}
                        aria-label="Share insight"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleFavorite(insight.id)}
                        aria-pressed={fav}
                        aria-label={fav ? "Remove from favorites" : "Save to favorites"}
                      >
                        <Heart
                          className={`h-4 w-4 ${
                            fav ? "fill-primary text-primary" : ""
                          }`}
                        />
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ElevateMoment;
